import Seo from "../components/Seo";
import { SectionHead, PrimaryLink, CTABand } from "../components/bits";
import { PilotWorkflow } from "../components/diagrams";
import { PILOT_PHASES } from "../content/pages";

export default function Pilot() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Smart Label Pilot Programme",
    step: PILOT_PHASES.map((p, i) => ({ "@type": "HowToStep", position: i + 1, name: p.name, text: p.body })),
  };

  return (
    <>
      <Seo
        path="/smart-label-pilot"
        title="Smart Label Pilot Programme"
        description="A structured smart-label pilot: define success metrics, configure labels and readers, run live shipments, review the evidence. Pilot before purchase."
        jsonLd={jsonLd}
      />
      <section className="border-b border-border bg-brand-surface">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-brand-navy sm:text-5xl">
            Pilot first. Scale on evidence.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate md:text-lg">
            Vendor claims — including ours — are hypotheses. The pilot programme replaces them with data from your own
            lanes, in four defined phases, before you commit to volume.
          </p>
          <div className="mt-8"><PrimaryLink to="/request-a-pilot" testid="pilot-cta-top">Start a pilot conversation</PrimaryLink></div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <PilotWorkflow />
        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {PILOT_PHASES.map((p, i) => (
            <article key={p.name} className="rounded-sm border border-border bg-white p-8" data-testid={`pilot-phase-${p.name.toLowerCase()}`}>
              <p className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">Phase 0{i + 1}</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-brand-navy">{p.name}</h2>
              <p className="mt-4 text-sm leading-relaxed text-brand-slate">{p.body}</p>
              <ul className="mt-5 space-y-2">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2 text-sm text-brand-slate">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-lime" aria-hidden="true" />{pt}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-brand-surface">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <SectionHead kicker="What a pilot costs" title="Small, scoped, and honest about it" />
          <div className="mt-8 max-w-3xl space-y-4 text-sm leading-relaxed text-brand-slate">
            <p>Pilot scope — label counts, gateways, duration — is agreed in the Define phase and priced per pilot. [Standard pilot pricing pending publication.]</p>
            <p>What you always get: provisioned labels, a GoAndTrack account for the pilot, weekly check-ins during Run, and an evidence pack at Review that says "scale", "mix" or "stop" — including when the honest answer is that smart labels aren't right for your lanes.</p>
          </div>
          <div className="mt-10"><PrimaryLink to="/request-a-pilot" testid="pilot-cta-bottom">Discuss Your Shipment</PrimaryLink></div>
        </div>
      </section>

      <CTABand title="Bring one difficult lane" sub="The best pilots start with the lane that causes the most arguments. Tell us about it." />
    </>
  );
}
